import React from 'react';

const pageStyle = { padding: '2rem', paddingTop: '8rem', maxWidth: '900px', margin: '0 auto' };

const EnglishPrivacy = () => (
  <div style={pageStyle}>
    <h1>Privacy Policy for Tomko</h1>
    <p><strong>Last updated:</strong> December 2025</p>

    <p>
      This Privacy Policy explains how Tomko collects, uses, stores, protects, and, where legally required,
      discloses information when you use the Tomko application and related services. We respect user privacy
      and process personal information only to the extent reasonably necessary to provide, secure, and support
      the service.
    </p>

    <p>
      By using Tomko, you acknowledge that you have read this Privacy Policy and agree that your information
      may be processed as described below. If you do not agree with this policy, you may choose not to use the
      service.
    </p>

    <h2>1. Scope</h2>
    <p>
      This policy applies to the Tomko application, Tomko Plus subscription features, account registration,
      authentication, technical support, and other service operations related to the functionality and security
      of Tomko.
    </p>

    <h2>2. Definitions</h2>
    <p>For the purposes of this Privacy Policy:</p>
    <ul>
      <li><strong>"Company", "we", "us", or "our"</strong> means the operator and producer of Tomko.</li>
      <li><strong>"Tomko" or "Service"</strong> means the Tomko application and related features or services.</li>
      <li><strong>"You" or "User"</strong> means the individual, organization, or authorized representative accessing or using Tomko.</li>
      <li><strong>"Personal Data"</strong> means information relating to an identified or identifiable individual.</li>
      <li><strong>"Account"</strong> means the unique user account created to access Tomko or parts of the service.</li>
      <li><strong>"Tomko Plus"</strong> means the paid subscription service that provides access to premium Tomko features.</li>
      <li><strong>"Device"</strong> means a phone, tablet, computer, or other device used to access Tomko.</li>
    </ul>

    <h2>3. Information We Collect</h2>
    <p>
      Tomko is designed around minimal data collection. We collect only the information needed to create and
      manage accounts, authenticate users, provide the service, diagnose technical issues, and maintain the
      stability and security of the application.
    </p>

    <h3>3.1 Account and Contact Information</h3>
    <p>When you create an account, we may ask for one of the following:</p>
    <ul>
      <li>mobile phone number; or</li>
      <li>email address.</li>
    </ul>
    <p>
      You do not need to provide both a mobile phone number and an email address to create an account. This
      information is used to verify your account, support sign-in, help recover access, and send service-related
      notices where necessary.
    </p>

    <h3>3.2 Technical Identifiers</h3>
    <p>To maintain reliable service operation, we may process the following technical identifiers:</p>
    <ul>
      <li><strong>User ID:</strong> a unique identifier associated with your account;</li>
      <li><strong>Device ID:</strong> a technical identifier associated with your device.</li>
    </ul>
    <p>
      These identifiers are used to detect, diagnose, and resolve bugs, crashes, sign-in issues, and other
      technical problems. They also help us improve service stability, performance, and security.
    </p>

    <h3>3.3 Sign in with Google</h3>
    <p>
      If you choose Sign in with Google in the Tomko mobile app, we receive only the information needed to
      create or sign in to your Tomko account. We require your <strong>email address</strong>,{" "}
      <strong>first name</strong>, and <strong>last name</strong> from Google for registration and
      authentication. We do not request or use other Google profile data beyond what is necessary for this
      purpose.
    </p>
    <p>
      We use this information only to sign you in, maintain your account, help you recover access, and provide
      Tomko services. We do not use Google account data for advertising, and we do not sell it to third
      parties. You can revoke Tomko&apos;s access to your Google account in your Google Account security
      settings and delete your Tomko account in the app.
    </p>

    <h3>3.4 Sign in with Apple</h3>
    <p>
      If you choose Sign in with Apple in the Tomko mobile app, we receive only the information needed to
      create or sign in to your Tomko account. We require your <strong>email address</strong>,{" "}
      <strong>first name</strong>, and <strong>last name</strong> from Apple for registration and
      authentication. If you use Hide My Email, we use the private relay address Apple provides solely to
      identify and communicate with your Tomko account as needed for the service.
    </p>
    <p>
      We use this information only to sign you in, maintain your account, help you recover access, and provide
      Tomko services. We do not use Apple account data for advertising, and we do not sell it to third
      parties. You can revoke Tomko&apos;s access in your Apple ID settings (Settings → [Your Name] → Sign-In
      &amp; Security → Apps Using Apple ID) and delete your Tomko account in the app.
    </p>

    <h2>4. How We Use Information</h2>
    <p>We may use your personal information for the following purposes:</p>
    <ul>
      <li>to create, maintain, and verify your user account;</li>
      <li>to authenticate you when you sign in to Tomko;</li>
      <li>to provide, operate, and maintain the service;</li>
      <li>to manage user settings, permissions, and subscription access;</li>
      <li>to recognize and activate Tomko Plus features;</li>
      <li>to diagnose and resolve technical errors, crashes, and service issues;</li>
      <li>to protect the security, integrity, and reliability of the service;</li>
      <li>to comply with legal obligations, lawful requests, and applicable agreements.</li>
    </ul>
    <p>
      We do not sell, rent, or disclose your personal information for third-party advertising or marketing
      purposes without your consent.
    </p>

    <h2>5. Information Sharing</h2>
    <p>
      We do not sell or rent your personal information. Your mobile phone number, email address, and related
      account information are processed only for the purposes described in this policy and in accordance with
      applicable law.
    </p>
    <p>
      We may disclose information if required by a valid request from a court, law enforcement authority, or
      other competent public authority, or where disclosure is reasonably necessary to comply with legal
      obligations, protect users, protect the public, or protect the rights, property, and security of the
      Company.
    </p>

    <h2>6. Data Retention</h2>
    <p>
      We retain personal information only for as long as necessary to fulfill the purposes described in this
      policy or for the period required by applicable law. When you delete your account, we will delete or,
      where appropriate, anonymize personal information associated with that account.
    </p>
    <p>
      We may retain certain information for a limited period where necessary to comply with legal obligations,
      resolve disputes, enforce agreements, or investigate security issues.
    </p>

    <h2>7. Data Transfers</h2>
    <p>
      Your information may be processed and stored in countries or regions where the Company, service
      infrastructure, or technical service providers operate. Where such processing occurs, we seek to apply
      appropriate safeguards and process information in accordance with applicable law.
    </p>

    <h2>8. Account and Data Deletion</h2>
    <p>
      You may delete your account at any time using the account deletion feature inside the Tomko application.
      When account deletion is initiated, we will begin removing the mobile phone number or email address, User
      ID, Device ID, and other account information associated with your account from our systems.
    </p>
    <p>
      If you need help deleting your account, correcting information, or submitting a privacy-related request,
      please contact us using the channels listed in the "Contact Us" section.
    </p>

    <h2>9. Tomko Plus and In-App Purchases</h2>
    <h3>9.1 Subscription Service</h3>
    <p>
      Tomko Plus is a paid subscription service that provides access to premium features in the Tomko
      application. Subscriptions are purchased through Google Play Store or Apple App Store, depending on your
      device platform.
    </p>

    <h3>9.2 Payment Processing</h3>
    <p>
      We do not directly process payment card details, banking information, or other financial payment
      credentials. Subscription purchases, renewals, cancellations, and payment transactions are handled by
      Google or Apple under their respective terms, privacy policies, and payment rules.
    </p>

    <h3>9.3 Auto-Renewal and Cancellation</h3>
    <p>
      Tomko Plus subscriptions may automatically renew unless cancelled in accordance with the applicable app
      store terms. To manage cancellation timing, renewal charges, and subscription status, review your
      subscription settings in Google Play Store or Apple App Store.
    </p>
    <ul>
      <li><strong>Android:</strong> Google Play Store → Account → Subscriptions.</li>
      <li><strong>iOS:</strong> Settings → [Your Name] → Subscriptions.</li>
    </ul>

    <h3>9.4 Prices, Trials, Grace Periods, and Refunds</h3>
    <p>
      Subscription prices, free trials, grace periods, price changes, billing timing, and refund procedures are
      governed by the information shown in the relevant app store and by Google or Apple policies. All payment
      details are displayed before purchase.
    </p>
    <p>
      <strong>No refunds are available for Tomko Plus subscriptions from the Company.</strong> Subscription
      purchases are final. If you submit a refund request through Google Play Store or Apple App Store, Google
      or Apple will review that request under its own policies. The Company cannot directly refund transactions
      processed through an app store.
    </p>
    <p>
      If you cancel a subscription, you may continue to access Tomko Plus until the end of the current paid
      billing period, subject to the relevant app store rules. You will not receive a refund for unused time in
      that period from the Company.
    </p>

    <h2>10. Lawful Disclosure of Personal Information</h2>
    <p>We may disclose personal information where reasonably necessary to:</p>
    <ul>
      <li>comply with a legal obligation, court order, or valid governmental request;</li>
      <li>protect and defend the rights, lawful interests, or property of the Company;</li>
      <li>detect, prevent, or investigate fraud, abuse, security risks, or service misuse;</li>
      <li>protect the safety of users or the public;</li>
      <li>protect against legal liability;</li>
      <li>support a merger, acquisition, asset transfer, or similar business transaction where necessary.</li>
    </ul>
    <p>
      If personal information is transferred as part of a business transaction, we will take reasonable steps to
      notify users where appropriate and to ensure continued protection of the information.
    </p>

    <h2>11. Security</h2>
    <p>
      We implement appropriate technical and organizational measures designed to protect personal information
      from unauthorized access, loss, alteration, disclosure, or destruction. However, no method of transmission
      over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
    </p>

    <h2>12. Children's Privacy</h2>
    <p>
      Tomko is not intended for children under the age of 13. We do not knowingly collect personally
      identifiable information from children under 13. If you are a parent or guardian and believe that a child
      has provided personal information to us, please contact us. If we confirm that such information was
      collected, we will take appropriate steps to delete it.
    </p>

    <h2>13. Third-Party Links and Services</h2>
    <p>
      Tomko may contain links to third-party websites, app stores, or services that we do not operate. We do
      not control and are not responsible for the content, privacy policies, or practices of those third
      parties. We recommend reviewing the privacy policy of any third-party service before using it.
    </p>

    <h2>14. Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time due to service changes, legal requirements, or
      operational updates. Updated versions will be posted on this page, and the "Last updated" date will be
      revised when changes take effect.
    </p>
    <p>
      We encourage you to review this policy periodically for changes.
    </p>

    <h2>15. Contact Us</h2>
    <p>
      If you have questions about this Privacy Policy, your personal information, account deletion, or how we
      process information, you may contact us through the following channels:
    </p>
    <ul>
      <li>Facebook: <a href="https://www.facebook.com/tomkomn/" target="_blank" rel="noopener noreferrer">Tomko Mongolia</a></li>
      <li>Email: contact@gobicraft.com</li>
      {/* <li>Phone: +976 8070-8476</li> */}
    </ul>
  </div>
);

const MongolianPrivacy = () => (
  <div style={pageStyle}>
    <h1>Нууцлалын бодлого</h1>
    <p><strong>Сүүлд шинэчилсэн:</strong> 2026 оны 5 дугаар сар</p>

    <p>
      Энэхүү Нууцлалын бодлого нь Tomko аппликейшн болон түүнтэй холбоотой үйлчилгээг ашиглах үед
      хэрэглэгчийн мэдээллийг хэрхэн цуглуулах, ашиглах, хадгалах, хамгаалах, мөн ямар нөхцөлд
      задруулах боломжтой талаар тайлбарлана. Бид хэрэглэгчийн хувийн мэдээллийг хүндэтгэж,
      мэдээллийг зөвхөн үйлчилгээ үзүүлэхэд шаардлагатай, үндэслэл бүхий хэмжээнд боловсруулах
      зарчмыг баримтална.
    </p>

    <p>
      Tomko үйлчилгээний үйлчилгээ үзүүлэгч нь Гобикрафт Системс ХХК бөгөөд компанийн хаяг нь
      Монгол улс, Улаанбаатар хот, Хан-Уул дүүрэг, 1-р хороо, Жапан Таун С2 болно.
    </p>

    <p>
      Та Tomko үйлчилгээг ашигласнаар энэхүү Нууцлалын бодлоготой танилцсан бөгөөд уг бодлогод
      заасан нөхцөлийн дагуу мэдээлэл боловсруулахыг зөвшөөрсөнд тооцогдоно. Хэрэв та энэхүү
      бодлогыг зөвшөөрөхгүй бол үйлчилгээг ашиглахгүй байх боломжтой.
    </p>

    <h2>1. Хамрах хүрээ</h2>
    <p>
      Энэхүү бодлого нь Tomko аппликейшн, Tomko Plus захиалгын үйлчилгээ, хэрэглэгчийн бүртгэл,
      нэвтрэлт баталгаажуулалт, техникийн дэмжлэг болон аппликейшний хэвийн ажиллагаатай холбоотой
      мэдээлэл боловсруулах үйл ажиллагаанд хамаарна.
    </p>

    <h2>2. Нэр томьёоны тайлбар</h2>
    <p>Энэхүү бодлогын хүрээнд дараах нэр томьёог доорх утгаар ойлгоно:</p>
    <ul>
      <li><strong>“Бид”, “Манай”, “Компани”</strong> гэж Tomko үйлчилгээг хөгжүүлж, ажиллуулж буй үйлчилгээ үзүүлэгч Гобикрафт Системс ХХК-ийг хэлнэ.</li>
      <li><strong>“Tomko”, “Үйлчилгээ”</strong> гэж Tomko аппликейшн болон түүнтэй холбоотой боломж, үйлчилгээний цогцыг хэлнэ.</li>
      <li><strong>“Та”, “Хэрэглэгч”</strong> гэж Tomko үйлчилгээнд бүртгүүлсэн, нэвтэрсэн, эсвэл ашиглаж буй хувь хүн, байгууллагын төлөөлөгчийг хэлнэ.</li>
      <li><strong>“Хувийн мэдээлэл”</strong> гэж тодорхойлогдсон эсвэл тодорхойлох боломжтой хувь хүнтэй холбоотой мэдээллийг хэлнэ.</li>
      <li><strong>“Бүртгэл”</strong> гэж Tomko үйлчилгээнд нэвтрэх, ашиглах зорилгоор хэрэглэгчид үүсгэсэн давтагдашгүй хэрэглэгчийн бүртгэлийг хэлнэ.</li>
      <li><strong>“Tomko Plus”</strong> гэж Tomko аппликейшний төлбөртэй нэмэлт боломж, premium эрх бүхий захиалгын үйлчилгээг хэлнэ.</li>
      <li><strong>“Төхөөрөмж”</strong> гэж гар утас, таблет, компьютер болон Tomko үйлчилгээнд нэвтрэх боломжтой бусад төхөөрөмжийг хэлнэ.</li>
    </ul>

    <h2>3. Бидний цуглуулах мэдээлэл</h2>
    <p>
      Tomko нь мэдээлэл хамгийн бага хэмжээнд цуглуулах зарчимд тулгуурлан ажилладаг. Бид таныг
      бүртгэх, нэвтрүүлэх, үйлчилгээг хэвийн үзүүлэх, техникийн доголдлыг оношлох, хэрэглэгчийн
      бүртгэлийг удирдахад шаардлагатай мэдээллийг л цуглуулна.
    </p>

    <h3>3.1 Бүртгэл болон холбоо барих мэдээлэл</h3>
    <p>Бүртгэл үүсгэх үед дараах мэдээллийн аль нэгийг шаардаж болно:</p>
    <ul>
      <li>гар утасны дугаар; эсвэл</li>
      <li>имэйл хаяг.</li>
    </ul>
    <p>
      Бүртгэл үүсгэхэд гар утасны дугаар болон имэйл хаягийг хоёуланг нь заавал өгөх шаардлагагүй.
      Эдгээр мэдээллийг хэрэглэгчийн бүртгэлийг баталгаажуулах, нэвтрэлт хийх, бүртгэлийг сэргээх,
      үйлчилгээтэй холбоотой зайлшгүй мэдэгдэл хүргэх зорилгоор ашиглана.
    </p>

    <h3>3.2 Техникийн танигч мэдээлэл</h3>
    <p>Үйлчилгээний найдвартай ажиллагааг хангах зорилгоор дараах техникийн мэдээллийг боловсруулж болно:</p>
    <ul>
      <li><strong>Хэрэглэгчийн ID:</strong> таны бүртгэлтэй холбоотой давтагдашгүй танигч;</li>
      <li><strong>Төхөөрөмжийн ID:</strong> тухайн төхөөрөмжтэй холбоотой техникийн танигч.</li>
    </ul>
    <p>
      Эдгээр мэдээллийг алдаа, гацалт, нэвтрэлтийн доголдол болон бусад техникийн асуудлыг илрүүлэх,
      оношлох, шийдвэрлэх, үйлчилгээний тогтвортой байдал, аюулгүй ажиллагааг сайжруулахад ашиглана.
    </p>

    <h3>3.3 Google-ээр нэвтрэх</h3>
    <p>
      Tomko гар утасны апп дээр Google-ээр нэвтрэхийг сонговол бид Tomko бүртгэл үүсгэх, нэвтрэхэд
      шаардлагатай мэдээллийг л хүлээн авна. Бүртгэл үүсгэх, баталгаажуулахад бид Google-оос таны{" "}
      <strong>имэйл хаяг</strong>, <strong>нэр</strong>, <strong>овог</strong>-ийг л шаардана. Энэ
      зорилгоос илүүтэй Google профайлын бусад мэдээллийг бид хүсдэггүй, ашиглахгүй.
    </p>
    <p>
      Бид энэ мэдээллийг зөвхөн нэвтрэх, бүртгэл хадгалах, хандалт сэргээх, Tomko үйлчилгээ үзүүлэхэд
      ашиглана. Бид Google-ийн мэдээллийг сурталчилгаанд ашиглахгүй, гуравдагч этгээдэд зарахгүй. Та
      Google Account-ийн аюулгүй байдлын тохиргооноос Tomko-д өгсөн эрхийг цуцлах, апп дотор Tomko
      бүртгэлээ устгах боломжтой.
    </p>

    <h3>3.4 Apple-ээр нэвтрэх</h3>
    <p>
      Tomko гар утасны апп дээр Apple-ээр нэвтрэхийг сонговол бид Tomko бүртгэл үүсгэх, нэвтрэхэд
      шаардлагатай мэдээллийг л хүлээн авна. Бүртгэл үүсгэх, баталгаажуулахад бид Apple-аас таны{" "}
      <strong>имэйл хаяг</strong>, <strong>нэр</strong>, <strong>овог</strong>-ийг л шаардана. Та Hide My
      Email ашигласан бол Apple-ийн өгсөн нууц дамжуулах имэйлийг зөвхөн таны Tomko бүртгэлийг таних,
      үйлчилгээтэй холбоотой шаардлагатай харилцаанд ашиглана.
    </p>
    <p>
      Бид энэ мэдээллийг зөвхөн нэвтрэх, бүртгэл хадгалах, хандалт сэргээх, Tomko үйлчилгээ үзүүлэхэд
      ашиглана. Бид Apple-ийн мэдээллийг сурталчилгаанд ашиглахгүй, гуравдагч этгээдэд зарахгүй. Та
      Apple ID тохиргооноос (Settings → [Таны нэр] → Sign-In &amp; Security → Apps Using Apple ID) Tomko-д
      өгсөн эрхийг цуцлах, апп дотор Tomko бүртгэлээ устгах боломжтой.
    </p>

    <h2>4. Мэдээллийг ашиглах зорилго</h2>
    <p>Бид таны хувийн мэдээллийг дараах зорилгоор ашиглаж болно:</p>
    <ul>
      <li>хэрэглэгчийн бүртгэл үүсгэх, удирдах, баталгаажуулах;</li>
      <li>Tomko үйлчилгээнд нэвтрүүлэх, таны хэн болохыг баталгаажуулах;</li>
      <li>үйлчилгээг хэвийн үзүүлэх, хэрэглэгчийн тохиргоо болон эрхийг удирдах;</li>
      <li>Tomko Plus зэрэг төлбөртэй боломжийн эрхийг таних, идэвхжүүлэх;</li>
      <li>техникийн алдаа, гацалт, доголдлыг оношлох, засварлах;</li>
      <li>үйлчилгээний аюулгүй байдал, тогтвортой ажиллагааг хангах;</li>
      <li>хууль, эрх бүхий байгууллагын шаардлага, гэрээний үүргээ биелүүлэх.</li>
    </ul>
    <p>
      Бид таны хувийн мэдээллийг хэрэглэгчийн зөвшөөрөлгүйгээр зар сурталчилгаа, гуравдагч этгээдийн
      маркетинг, арилжааны сурталчилгааны зорилгоор худалдах, түрээслэх, дамжуулахгүй.
    </p>

    <h2>5. Мэдээлэл хуваалцах бодлого</h2>
    <p>
      Бид таны гар утасны дугаар, имэйл хаяг зэрэг хувийн мэдээллийг гуравдагч этгээдэд худалдахгүй,
      түрээслэхгүй, сурталчилгааны зорилгоор дамжуулахгүй. Хувийн мэдээллийг зөвхөн энэхүү бодлогод
      заасан зорилгоор, шаардлагатай тохиолдолд, холбогдох хууль тогтоомжид нийцүүлэн боловсруулна.
    </p>
    <p>
      Дараах нөхцөлд мэдээлэл задруулах шаардлага үүсэж болно: шүүх, прокурор, хууль сахиулах болон
      бусад эрх бүхий байгууллагын хүчин төгөлдөр шаардлага; хууль ёсны үүргээ биелүүлэх хэрэгцээ;
      хэрэглэгч, олон нийт, эсвэл компанийн эрх, өмч, аюулгүй байдлыг хамгаалах үндэслэл.
    </p>

    <h2>6. Мэдээлэл хадгалах хугацаа</h2>
    <p>
      Бид хувийн мэдээллийг тухайн мэдээллийг цуглуулсан зорилгыг биелүүлэхэд шаардлагатай хугацаанд,
      эсвэл холбогдох хууль тогтоомжоор хадгалахыг шаардсан хугацаанд хадгална. Хэрэглэгч бүртгэлээ
      устгасан тохиолдолд бид бүртгэлтэй холбоотой хувийн мэдээллийг устгах буюу боломжтой тохиолдолд
      таних боломжгүй хэлбэрт шилжүүлнэ.
    </p>
    <p>
      Маргаан шийдвэрлэх, хууль ёсны үүргээ биелүүлэх, аюулгүй байдлын зөрчлийг шалгах зэрэг үндэслэл
      байгаа бол тодорхой мэдээллийг хуульд нийцүүлэн шаардлагатай хугацаанд хадгалж болно.
    </p>

    <h2>7. Мэдээлэл шилжүүлэх</h2>
    <p>
      Таны мэдээлэл Компани, үйлчилгээний дэд бүтэц, эсвэл техникийн үйлчилгээ үзүүлэгчдийн байрлаж буй
      улс, бүс нутагт боловсруулагдаж, хадгалагдаж болно. Ийм тохиолдолд бид мэдээллийг хамгаалах
      зохих арга хэмжээг хэрэгжүүлж, холбогдох хууль тогтоомжид нийцүүлэн мэдээлэл боловсруулахыг
      эрмэлзэнэ.
    </p>

    <h2>8. Бүртгэл болон мэдээлэл устгах эрх</h2>
    <p>
      Та Tomko аппликейшн доторх бүртгэл устгах боломжийг ашиглан бүртгэлээ хүссэн үедээ устгаж болно.
      Бүртгэл устгах үед таны бүртгэлтэй холбоотой гар утасны дугаар эсвэл имэйл хаяг, хэрэглэгчийн ID,
      төхөөрөмжийн ID болон бусад бүртгэлийн мэдээллийг манай системээс устгах ажиллагаа эхэлнэ.
    </p>
    <p>
      Хэрэв бүртгэл устгах, мэдээлэл засварлах, эсвэл мэдээлэлтэй холбоотой хүсэлт гаргахад тусламж
      шаардлагатай бол энэхүү бодлогын “Бидэнтэй холбогдох” хэсэгт заасан сувгаар бидэнд хандана уу.
    </p>

    <h2>9. Tomko Plus болон апп доторх худалдан авалт</h2>
    <h3>9.1 Захиалгын үйлчилгээ</h3>
    <p>
      Tomko Plus нь Tomko аппликейшний нэмэлт premium боломжуудыг ашиглах эрх олгодог төлбөртэй
      захиалгын үйлчилгээ юм. Захиалга нь хэрэглэгчийн төхөөрөмжийн платформоос хамааран Google Play
      Store эсвэл Apple App Store-оор дамжин хийгдэнэ.
    </p>

    <h3>9.2 Төлбөр</h3>
    <p>
      Бид хэрэглэгчийн банкны карт, төлбөрийн хэрэгсэл, санхүүгийн дэлгэрэнгүй мэдээллийг шууд
      боловсруулдаггүй. Захиалгын худалдан авалт, сунгалт, цуцлалт болон төлбөрийн гүйлгээг Google,
      Apple, эсвэл Монгол төгрөгөөр (MNT) хийгдэх гүйлгээний хувьд Голомт банкны интернэт төлбөрийн
      үйлчилгээ өөрсдийн үйлчилгээний нөхцөл, нууцлалын бодлого, төлбөрийн журмын дагуу боловсруулна.
    </p>

    <h3>9.3 Автомат сунгалт ба цуцлалт</h3>
    <p>
      Tomko Plus захиалга цуцлагдаагүй тохиолдолд тухайн app store-ийн нөхцөлийн дагуу автоматаар
      сунгагдаж болно. Захиалгаа цуцлах, сунгалтын төлбөрөөс зайлсхийх, төлбөр нэхэмжлэгдэх хугацааг
      тодруулахын тулд Google Play Store эсвэл Apple App Store дахь захиалгын тохиргоо, тухайн
      платформын нөхцөлтэй танилцана уу.
    </p>
    <ul>
      <li><strong>Android:</strong> Google Play Store → Account → Subscriptions хэсэгт захиалгаа удирдана.</li>
      <li><strong>iOS:</strong> Settings → [Your Name] → Subscriptions хэсэгт захиалгаа удирдана.</li>
    </ul>

    <h3>9.4 Үнэ, туршилтын хугацаа, буцаан олголт</h3>
    <p>
      Захиалгын үнэ, үнэгүй туршилтын хугацаа, хөнгөлөлттэй хугацаа, үнийн өөрчлөлт, буцаан олголтын
      нөхцөл нь тухайн app store дээр харуулсан мэдээлэл, Google, Apple, эсвэл MNT гүйлгээнд хэрэглэгдэх
      Голомт банкны интернэт төлбөрийн үйлчилгээний мөрдөж буй бодлогоор зохицуулагдана. Бүх төлбөрийн
      мэдээллийг худалдан авалт хийхээс өмнө хэрэглэгчид харуулна.
    </p>
    <p>
      Tomko Plus захиалгын төлбөр нь эцсийн бөгөөд Компани захиалгын төлбөрт буцаан олголт хийхгүй.
      Хэрэв хэрэглэгч Google Play Store эсвэл Apple App Store-оор дамжуулан буцаан олголтын хүсэлт
      гаргавал тухайн хүсэлтийг Google эсвэл Apple өөрсдийн бодлого, журмын дагуу шийдвэрлэнэ. Компани
      app store-оор хийгдсэн төлбөрийн гүйлгээг шууд буцаан олгох боломжгүй.
    </p>

    <h2>10. Хувийн мэдээлэл задруулах хууль ёсны үндэслэл</h2>
    <p>Бид дараах нөхцөлд хувийн мэдээллийг шаардлагатай хэмжээнд задруулж болно:</p>
    <ul>
      <li>хууль, шүүхийн шийдвэр, эрх бүхий байгууллагын хүчин төгөлдөр шаардлагыг биелүүлэх;</li>
      <li>Компанийн эрх, хууль ёсны ашиг сонирхол, өмч хөрөнгийг хамгаалах;</li>
      <li>үйлчилгээтэй холбоотой залилан, зөрчил, аюулгүй байдлын эрсдэлийг илрүүлэх, шалгах;</li>
      <li>хэрэглэгчид болон олон нийтийн аюулгүй байдлыг хамгаалах;</li>
      <li>нэгдэх, худалдан авах, хөрөнгө шилжүүлэх зэрэг бизнесийн хэлцлийн хүрээнд шаардлагатай болсон тохиолдол.</li>
    </ul>
    <p>
      Бизнесийн хэлцлийн улмаас хувийн мэдээлэл шилжих нөхцөл үүсвэл бид боломжит хэмжээнд хэрэглэгчдэд
      урьдчилан мэдэгдэж, мэдээлэл цаашид хэрхэн хамгаалагдах талаар зохих арга хэмжээ авна.
    </p>

    <h2>11. Мэдээллийн аюулгүй байдал</h2>
    <p>
      Бид таны хувийн мэдээллийг зөвшөөрөлгүй хандах, алдагдах, өөрчлөгдөх, задрах, устах эрсдэлээс
      хамгаалахын тулд зохих техникийн болон зохион байгуулалтын арга хэмжээг хэрэгжүүлнэ. Гэсэн хэдий ч
      интернетээр дамжуулах болон цахим хэлбэрээр хадгалах аливаа арга нь бүрэн эрсдэлгүй биш тул бид
      мэдээллийн аюулгүй байдлыг 100 хувь баталгаажуулах боломжгүй.
    </p>

    <h2>12. Хүүхдийн нууцлал</h2>
    <p>
      Бид 13-аас доош насны хүүхдээс хувийн мэдээлэл санаатайгаар цуглуулдаггүй. Хэрэв эцэг эх, асран хамгаалагч нь хүүхэд хувийн
      мэдээлэл өгсөн гэж үзвэл бидэнтэй холбогдоно уу. Ийм мэдээлэл цугларсан нь тогтоогдвол бид тухайн
      мэдээллийг устгах зохих арга хэмжээг авна.
    </p>

    <h2>13. Гуравдагч этгээдийн холбоос ба үйлчилгээ</h2>
    <p>
      Tomko үйлчилгээ нь бидний удирддаггүй гуравдагч этгээдийн вэбсайт, app store, эсвэл бусад
      үйлчилгээ рүү чиглэсэн холбоос агуулж болно. Тэдгээр үйлчилгээний агуулга, мэдээлэл боловсруулах
      бодлого, аюулгүй ажиллагаанд бид хяналт тавихгүй бөгөөд хариуцлага хүлээхгүй. Та гуравдагч
      этгээдийн үйлчилгээ ашиглахын өмнө тухайн үйлчилгээний нууцлалын бодлоготой танилцахыг зөвлөж байна.
    </p>

    <h2>14. Нууцлалын бодлогын өөрчлөлт</h2>
    <p>
      Бид энэхүү Нууцлалын бодлогыг үйлчилгээний өөрчлөлт, хууль тогтоомжийн шаардлага, эсвэл дотоод
      үйл ажиллагааны шинэчлэлтэй холбоотойгоор үе үе шинэчилж болно. Шинэчилсэн хувилбарыг энэ хуудсанд
      нийтэлж, “Сүүлд шинэчилсэн” огноог өөрчилснөөр өөрчлөлт хүчин төгөлдөр болно.
    </p>
    <p>
      Та энэхүү бодлоготой үе үе танилцаж, өөрчлөлт орсон эсэхийг шалгаж байхыг зөвлөж байна.
    </p>

    <h2>15. Бидэнтэй холбогдох</h2>
    <p>
      Энэхүү Нууцлалын бодлого, таны хувийн мэдээлэл, бүртгэл устгах хүсэлт, эсвэл мэдээлэл боловсруулахтай
      холбоотой аливаа асуултаар бидэнтэй дараах сувгаар холбогдоно уу:
    </p>
    <ul>
      <li>Facebook: <a href="https://www.facebook.com/tomkomn/" target="_blank" rel="noopener noreferrer">Tomko Learn a Language</a></li>
      <li>Имэйл: contact@gobicraft.com</li>
      <li>Үйлчилгээ үзүүлэгч: Гобикрафт Системс ХХК</li>
      <li>Хаяг: Монгол улс, Улаанбаатар хот, Хан-Уул дүүрэг, 1-р хороо, Жапан Таун С2</li>
      {/* <li>Утас: +976 8070-8476</li> */}
    </ul>
  </div>
);

const Privacy = ({ language = 'en' }) => {
  return language === 'mn' ? <MongolianPrivacy /> : <EnglishPrivacy />;
};

export default Privacy;
